import { Link } from "react-router-dom";
import { routes } from "~/lib/routes";
import { cn } from "~/lib/utils";
import { ActionsMenu } from "~/components/ui/actions-menu";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { EditableInput, EditableTextarea } from "~/components/ui/inline-edit";

interface MapViewInfo {
  name: string;
  description: string | null;
  curatedBy?: { displayName: string } | null;
  id?: string;
  landingPage?: { publishedAt: Date | null } | null;
}

interface AdventureMapViewProps {
  map: MapViewInfo;
  /** URL for back navigation. If omitted, back button is hidden. */
  backTo?: string;
  onUpdate?: (data: { name?: string; description?: string | null }) => Promise<void>;
  onShare?: () => void;
  onCollaborators?: () => void;
  onDelete?: () => void;
  onTransfer?: () => void;
  canPublishLandingPage?: boolean;
  children?: React.ReactNode; // For places list
}

/**
 * View for an adventure map in panel context. Shows map info in a readable format
 * with click-to-edit interactions, replacing the traditional form approach.
 */
export function AdventureMapView({
  map,
  backTo,
  onUpdate,
  onShare,
  onCollaborators,
  onDelete,
  onTransfer,
  canPublishLandingPage = false,
  children,
}: AdventureMapViewProps) {
  const canEdit = !!onUpdate;

  return (
    <div className="flex flex-col gap-4">
      {/* Header: Back + Title + More menu */}
      <div className="px-6 pt-4">
        <div className="flex items-start gap-3">
          {backTo && (
            <Button asChild variant="ghost" size="icon-sm" className="mt-0.5">
              <Link to={backTo} title="Back" aria-label="Back">
                <Icon i="back" className="size-5" />
              </Link>
            </Button>
          )}
          <div className="flex-1 min-w-0">
            <EditableInput
              value={map.name}
              onSave={(v) => onUpdate?.({ name: v! }) ?? Promise.resolve()}
              placeholder="Map name"
              showIcon={false}
              required
              canEdit={canEdit}
              className="text-xl font-semibold text-foreground"
            />
            {map.curatedBy && (
              <p className="text-sm text-muted-foreground mt-1">Curated by {map.curatedBy.displayName}</p>
            )}
          </div>
          <ActionsMenu
            actions={[
              [
                onShare && { icon: "share" as const, label: "Share", onClick: onShare },
                onCollaborators && { icon: "users" as const, label: "Collaborators", onClick: onCollaborators },
              ],
              [
                onTransfer && {
                  icon: "transfer" as const,
                  label: "Transfer ownership",
                  onClick: onTransfer,
                  variant: "destructive" as const,
                },
                onDelete && {
                  icon: "trash" as const,
                  label: "Delete map",
                  onClick: onDelete,
                  variant: "destructive" as const,
                },
              ],
            ]}
          />
        </div>
      </div>

      {/* Action buttons */}
      {(canPublishLandingPage || onShare) && (
        <div className="px-6 flex gap-2">
          {canPublishLandingPage && map.id && (
            <Button asChild variant="outline" className="flex-1">
              <Link to={routes.to.maps.landing(map.id)}>
                <Icon i="layout" className="size-4" />
                {map.landingPage?.publishedAt ? "Edit landing page" : "Create landing page"}
              </Link>
            </Button>
          )}
          {onShare && (
            <Button
              variant="outline"
              onClick={onShare}
              title={canPublishLandingPage ? "Share this map" : undefined}
              aria-label={canPublishLandingPage ? "Share this map" : undefined}
              size={canPublishLandingPage ? "icon" : undefined}
              className={cn({ "flex-1": !canPublishLandingPage })}
            >
              <Icon i="share" className="size-4" />
              {!canPublishLandingPage && "Share"}
            </Button>
          )}
        </div>
      )}

      {/* Description */}
      {(map.description || canEdit) && (
        <div className="px-6">
          <EditableTextarea
            value={map.description}
            onSave={(v) => onUpdate?.({ description: v }) ?? Promise.resolve()}
            placeholder="Add a description..."
            addLabel="+ Add description"
            canEdit={canEdit}
          />
        </div>
      )}

      {/* Children (places list, etc.) */}
      {children}
    </div>
  );
}
